/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { uploaderService } from './index'
import CircularProgress from '../../components/CircularProgress'
import FileIcon from '../../components/FileIcon'
import ModalInfo from '../../components/ModalInfo'

interface Position {
  x: number
  y: number
}

/**
 * UploaderWidget Component
 *
 * Displays the upload queue UI, showing active uploads with progress bars,
 * and providing controls to abort uploads or close the widget.
 *
 * Features:
 * - Draggable window with position persistence
 * - Circular progress indicators
 * - Confirmation modals for close/cancel actions
 * - Translation support
 * - Beforeunload warning when uploads active
 * - File type icons
 *
 * This component matches the legacy AngularJS uploader directive functionality.
 */
export const UploaderWidget: React.FC = () => {
  const { t } = useTranslation()
  const [isDisplayed, setIsDisplayed] = useState<boolean>(() => uploaderService.state.isDisplayed)
  const [isMinimized, setIsMinimized] = useState<boolean>(() => uploaderService.state.isMinimized)
  const [uploadQueue, setUploadQueue] = useState<any[]>(() => uploaderService.uploadQueue)
  const [position, setPosition] = useState<Position>(() => {
    const saved = localStorage.getItem('uploader.position')
    if (saved) {
      try {
        const pos = JSON.parse(saved)
        if (
          typeof pos.x === 'number' &&
          typeof pos.y === 'number' &&
          pos.x >= 0 &&
          pos.y >= 0 &&
          pos.x < window.innerWidth &&
          pos.y < window.innerHeight
        ) {
          return pos as Position
        }
      } catch (e) {
        // Invalid JSON, use default
      }
    }
    const defaultX = Math.max(0, window.innerWidth - 450)
    const defaultY = Math.max(0, window.innerHeight - 320)
    return { x: defaultX, y: defaultY }
  })
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false)
  const [showCancelItemModal, setShowCancelItemModal] = useState<boolean>(false)
  const pendingAbortQueueIdRef = useRef<number | null>(null)
  const widgetRef = useRef<HTMLDivElement>(null)

  // Sync state with uploaderService - run on every interval tick
  useEffect(() => {
    const interval = setInterval(() => {
      // Always sync from service - this ensures we never have stale state
      setIsDisplayed(uploaderService.state.isDisplayed)
      setIsMinimized(uploaderService.state.isMinimized)

      // Use a functional update that bails out when the queue reference and length
      // haven't changed.  Previously `setUploadQueue([])` was called with a brand-new
      // array reference every tick even when the queue was already empty, causing
      // continuous re-renders and making every inline function prop of the ModalInfo
      // children a new reference on every tick.  That forced Modal's useEffect
      // (which lists handleClose as a dependency) to tear-down and re-mount its
      // DOM side-effects (overflow, modal-open class) 10 times per second — the
      // "state leak" that was the routing regression.
      const queue = uploaderService.uploadQueue
      setUploadQueue((prev) => {
        if (prev === queue) return prev
        if (prev.length === 0 && queue.length === 0) return prev // already empty — bail out
        return [...queue]
      })
    }, 100)

    // Listen for new upload started event to immediately show widget
    const handleNewUpload = (event: Event) => {
      // Force immediate update
      setIsDisplayed(true)
      setUploadQueue([...uploaderService.uploadQueue])
    }

    window.addEventListener('uploader.newUploadStarted', handleNewUpload as EventListener)

    return () => {
      clearInterval(interval)
      window.removeEventListener('uploader.newUploadStarted', handleNewUpload as EventListener)
    }
  }, [])

  // Check for active uploads
  const hasActiveUploads = useCallback(() => {
    return uploadQueue.some((q: any) => !q.isCompleted && !q.isFailed && !q.isAborted)
  }, [uploadQueue])

  // Beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads()) {
        e.preventDefault()
        e.returnValue = '' // Chrome requires returnValue to be set
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasActiveUploads])

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('uploader.position', JSON.stringify(position))
  }, [position])

  // Dragging handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!widgetRef.current) return
    const rect = widgetRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset])

  const handleMinimize = () => {
    uploaderService.state.isMinimized = !uploaderService.state.isMinimized
    setIsMinimized(uploaderService.state.isMinimized)
  }

  const handleClose = () => {
    if (hasActiveUploads()) {
      setShowCloseModal(true)
      return
    }
    uploaderService.clearFilesAndClose()
    setIsDisplayed(false)
  }

  const handleAbortQueue = (queueId: number) => {
    pendingAbortQueueIdRef.current = queueId
    setShowCancelItemModal(true)
  }

  // Stable callbacks for the confirmation modals.  These must NOT be recreated on
  // every render — if they were, ModalInfo would pass a new handleClose reference
  // to Modal on every tick of the 100 ms interval, causing Modal's useEffect to
  // run cleanup (setting overflow/modal-open) 10 times per second (the state leak
  // responsible for the routing regression).
  const confirmClose = useCallback(() => {
    uploaderService.clearFilesAndClose()
    setIsDisplayed(false)
  }, [])

  const dismissCloseModal = useCallback(() => {
    setShowCloseModal(false)
  }, [])

  const confirmCancelItem = useCallback(() => {
    if (pendingAbortQueueIdRef.current !== null) {
      uploaderService.abortQueue(pendingAbortQueueIdRef.current)
    }
    pendingAbortQueueIdRef.current = null
    setShowCancelItemModal(false)
  }, [])

  const dismissCancelItemModal = useCallback(() => {
    setShowCancelItemModal(false)
    pendingAbortQueueIdRef.current = null
  }, [])

  if (!isDisplayed) {
    return null
  }

  // Deduplicate queue entries by both queue.id AND filename
  // This prevents duplicate entries when the same file is added multiple times with different queue IDs
  const deduplicatedQueue = uploadQueue.reduce((acc: any[], queue: any) => {
    const file = queue.files?.[0]
    if (!file) return acc

    // Check for duplicate by queue.id first
    const existingByIdIndex = acc.findIndex((q) => q.id === queue.id)
    if (existingByIdIndex !== -1) {
      // Replace existing entry with the newer one
      acc[existingByIdIndex] = queue
      return acc
    }

    // Check for duplicate by filename (to catch different queue IDs for same file)
    const existingByNameIndex = acc.findIndex((q) => q.files?.[0]?.name === file.name)
    if (existingByNameIndex !== -1) {
      // Prefer in-progress over completed (completed might be optimistic/premature)
      // Keep the one that's still tracking actual upload progress
      const existing = acc[existingByNameIndex]
      const queueIsActive = !queue.isCompleted && !queue.isFailed && !queue.isAborted
      const existingIsActive = !existing.isCompleted && !existing.isFailed && !existing.isAborted

      if (queueIsActive && !existingIsActive) {
        // Replace with the in-progress one (it's tracking real API progress)
        acc[existingByNameIndex] = queue
      } else if (!queueIsActive && existingIsActive) {
        // Keep existing in-progress one (it's tracking real API progress)
        // Don't replace
      } else {
        // Both same status, keep the newer one
        acc[existingByNameIndex] = queue
      }
      return acc
    }

    // New unique entry
    acc.push(queue)
    return acc
  }, [])

  const activeCount = deduplicatedQueue.filter((q: any) => !q.isCompleted && !q.isFailed && !q.isAborted).length
  // All uploads are finished when every item is either completed, failed, or aborted
  const allFinished =
    deduplicatedQueue.length > 0 && deduplicatedQueue.every((q: any) => q.isCompleted || q.isFailed || q.isAborted)

  return (
    <>
      <div
        ref={widgetRef}
        id="rcm-uploader"
        className={isMinimized ? 'isMinimized' : ''}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          userSelect: isDragging ? 'none' : 'auto',
          zIndex: 999999,
          pointerEvents: 'auto'
        }}
      >
        {/* Header */}
        <div className="header" onMouseDown={handleMouseDown}>
          <span className="fs14">{allFinished ? t('UPLOADER_UPLOAD_TITLE') : t('UPLOADER_UPLOADING_TITLE')}</span>
          {/* Minimize icon: use FontAwesome minus icon for legacy parity */}
          <i
            className={`fa ${isMinimized ? 'fa-angle-up' : 'fa-angle-down'}`}
            title={
              isMinimized ? t('UPLOADER_MAXIMIZE_TOOLTIP') || 'Maximize' : t('UPLOADER_MINIMIZE_TOOLTIP') || 'Minimize'
            }
            onClick={(e) => {
              e.stopPropagation()
              handleMinimize()
            }}
          />
          {/* Close icon: use FontAwesome times icon for legacy parity */}
          <i
            className="fa close-icon"
            title={t('UPLOADER_CLOSE_TOOLTIP') || 'Close'}
            onClick={(e) => {
              e.stopPropagation()
              handleClose()
            }}
          />
        </div>
        {!isMinimized && (
          <div className="content">
            {deduplicatedQueue.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', margin: '20px 0' }}>
                {(() => {
                  return 'No uploads'
                })()}
              </p>
            ) : (
              <ul>
                {deduplicatedQueue.map((queue: any) => {
                  // Render each file in the queue (matching legacy behavior)
                  // Legacy template: <li ng-repeat="file in queue.files">
                  const files = queue.files || []
                  if (files.length === 0) return null

                  return files.map((file: any, fileIndex: number) => {
                    if (!file) return null

                    const progressStr = file.progress ? file.progress(queue.id) : '0'
                    const progress = progressStr || '0'
                    const progressNum = Math.max(0, Math.min(100, parseInt(progress, 10)))

                    return (
                      <li key={`${queue.id}-${fileIndex}`}>
                        {/* File info */}
                        <div className="thumb-image">
                          <FileIcon
                            fileName={file.name}
                            fileType={'DOCUMENT' as const}
                            isValid={true}
                            isObsolete={false}
                          />
                        </div>
                        <span className="name" title={file.name}>
                          {file.name}
                        </span>
                        {/* Progress Circle or Checkmark */}
                        <div
                          className={`progress-wrapper${!queue.isCompleted && !queue.isFailed && !queue.isAborted ? ' in-progress' : queue.isFailed ? ' failed' : ''}`}
                        >
                          {queue.isCompleted ? (
                            <div className="upload-completed">&nbsp;</div>
                          ) : queue.isFailed ? (
                            <div className="upload-canceled">{t('UPLOADER_UPLOAD_FAILED')}</div>
                          ) : queue.isAborted ? (
                            <div className="upload-canceled">⊗</div>
                          ) : (
                            <>
                              <div className="progress-bar-component">
                                <CircularProgress
                                  key={`progress-${queue.id}-${fileIndex}`}
                                  progress={progressNum}
                                  size={35}
                                  strokeWidth={8}
                                  trailWidth={8}
                                  color="#4a90e2"
                                  showText={true}
                                />
                              </div>
                              {/* Only show cancel button on first file to avoid multiple cancel buttons */}
                              {fileIndex === 0 && (
                                <div className="cancel-upload-wrapper">
                                  <span
                                    className="cancel-upload"
                                    title={t('UPLOADER_CANCEL_UPLOAD_TOOLTIP') || 'Cancel upload'}
                                    onClick={() => handleAbortQueue(queue.id)}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </li>
                    )
                  })
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {showCloseModal && (
        <ModalInfo
          titleMsg="UPLOADER_CANCEL_UPLOAD_TITLE"
          msg="UPLOADER_CANCEL_UPLOAD_QUESTION"
          modalStatus={dismissCloseModal}
          okFn={confirmClose}
          cancelFn={dismissCloseModal}
          showCancel={true}
          okTxt="YES"
          cancelTxt="NO"
        />
      )}

      {showCancelItemModal && (
        <ModalInfo
          titleMsg="UPLOADER_CANCEL_UPLOAD_TITLE"
          msg="UPLOADER_CANCEL_UPLOAD_QUESTION"
          modalStatus={dismissCancelItemModal}
          okFn={confirmCancelItem}
          cancelFn={dismissCancelItemModal}
          showCancel={true}
          okTxt="YES"
          cancelTxt="NO"
        />
      )}
    </>
  )
}

export default UploaderWidget
